import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNotes } from "../../hooks/useNotes";
import { StickyNote, Search, Plus, Filter, Edit3, Trash2, Clock, Pin } from "lucide-react";
import NoteDialog from "./NoteDialog";
import ExpandedNoteEditor from "./ExpandedNoteEditor";

const NotesManager = ({ auth }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [filterText, setFilterText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isEditing, setIsEditing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  // NEW: State for expanded note
  const [expandedNote, setExpandedNote] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    noteDate: new Date().toISOString().split("T")[0],
    category: "Allgemein",
    isGeneral: false,
  });

  const workspaceId = localStorage.getItem("workspaceId");
  const { notes, loading, error, addNote, fetchNotes, updateNote, deleteNote } = useNotes({
    workspaceId: workspaceId,
    token: auth.user?.id_token,
  });

  useEffect(() => { fetchNotes(); }, []);

  // Sync URL ID with expandedNote
  useEffect(() => {
    if (id && notes.length > 0) {
      // note.id might be number or string, useParams id is string
      // using loose equality for safety or String() comparison
      const found = notes.find(n => String(n.id) === String(id));
      if (found) {
        setExpandedNote(found);
        setShowAddForm(false); // Make sure dialog is closed if we are deep linking
      }
    } else if (!id) {
      setExpandedNote(null);
    }
  }, [id, notes]);

  // --- Logic: Pinned first, then by date ---
  const sortedAndFilteredNotes = useMemo(() => {
    return notes
      .filter((n) => {
        const matchesText = n.title?.toLowerCase().includes(filterText.toLowerCase()) ||
          n.content?.toLowerCase().includes(filterText.toLowerCase());
        const matchesCat = selectedCategory === "All" || n.category === selectedCategory;
        return matchesText && matchesCat;
      })
      .sort((a, b) => {
        if (a.isGeneral !== b.isGeneral) return b.isGeneral ? -1 : 1;
        return new Date(b.noteDate) - new Date(a.noteDate);
      });
  }, [notes, filterText, selectedCategory]);

  const categories = useMemo(() => {
    const cats = new Set(notes.map(n => n.category).filter(Boolean));
    return ["All", ...Array.from(cats)];
  }, [notes]);

  // --- Actions ---
  const handleSave = async (dataToSave = formData) => {
    if (!dataToSave.title) return;

    // Check if we are saving from the Expanded View (which passes dataToSave)
    // or from the Modal (which uses state formData directly if not passed, but we standardized to pass data)

    // If saving from expanded view:
    if (expandedNote) {
      if (dataToSave.id) {
        await updateNote(dataToSave.id, dataToSave);
      } else {
        await addNote({ ...dataToSave, workspaceId });
      }
      // setExpandedNote(null); // No longer needed as we navigate if saving implies closing
      navigate('/Notes'); // Return to list logic
      return;
    }

    // If saving from Modal:
    if (isEditing) {
      await updateNote(formData.id, formData);
    } else {
      await addNote({ ...formData, workspaceId });
    }
    resetForm();
  };

  const handleTogglePin = async (e, note) => {
    e.stopPropagation();
    await updateNote(note.id, { ...note, isGeneral: !note.isGeneral });
  };

  const resetForm = () => {
    setFormData({ title: "", content: "", noteDate: new Date().toISOString().split("T")[0], category: "Allgemein", isGeneral: false });
    setIsEditing(false);
    setShowAddForm(false);
  };

  const startEdit = (note) => {
    setFormData({
      ...note,
      noteDate: note.noteDate ? note.noteDate.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setIsEditing(true);
    setShowAddForm(true);
  };

  // Called from NoteDialog to switch to Expanded View
  const handleExpandFromDialog = (currentFormData) => {
    setShowAddForm(false); // Close dialog

    if (currentFormData.id) {
      navigate(`/Notes/${currentFormData.id}`);
    } else {
      // New note (unsaved) - we can't deep link yet, so just local state
      setExpandedNote(currentFormData);
    }
  };

  // If Expanded View is active, show ONLY that
  if (expandedNote) {
    return (
      <ExpandedNoteEditor
        note={expandedNote}
        onBack={() => {
          if (id) {
            navigate('/Notes');
          } else {
            setExpandedNote(null);
          }
        }}
        onSave={handleSave}
      />
    );
  }

  return (
    <div className="flex-grow-1 bg-light p-4" style={{ fontFamily: "'Segoe UI', Roboto, sans-serif" }}>
      <div className="container-fluid max-width-xl">

        {/* --- HEADER --- */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="bg-white rounded-4 shadow-sm p-3 d-flex flex-wrap align-items-center gap-3">
              <div className="d-flex align-items-center gap-2 me-3">
                <div className="bg-primary bg-opacity-10 p-2 rounded-3">
                  <StickyNote className="text-primary" size={24} />
                </div>
                <h4 className="mb-0 fw-bold">Notizen</h4>
              </div>

              <div className="flex-grow-1 position-relative">
                <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={18} />
                <input
                  className="form-control border-0 bg-light ps-5 rounded-3"
                  placeholder="Notizen durchsuchen..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>

              <button className="btn btn-primary rounded-3 px-4 d-flex align-items-center gap-2" onClick={() => setShowAddForm(true)}>
                <Plus size={18} /> Neue Notiz
              </button>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* --- SIDEBAR --- */}
          <div className="col-lg-3">
            <div className="card border-0 shadow-sm rounded-4 p-3 sticky-top" style={{ top: '2rem' }}>
              <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-muted">
                <Filter size={16} /> Kategorien
              </h6>
              <div className="d-grid gap-1">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`btn btn-sm text-start rounded-3 px-3 py-2 border-0 transition-all ${selectedCategory === cat ? 'bg-primary text-white' : 'hover-bg-light'}`}
                  >
                    {cat === "All" ? "Alle Notizen" : cat}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* --- MAIN CONTENT --- */}
          <div className="col-lg-9">
            {error && <div className="alert alert-danger rounded-4">{error}</div>}
            <div className="row g-3">
              {sortedAndFilteredNotes.map((note) => (
                <div key={note.id} className="col-md-6 col-xl-4">
                  <div className={`card border-0 shadow-sm rounded-4 h-100 transition-all hover-shadow ${note.isGeneral ? 'pinned-active' : ''}`}>
                    <div className="card-body p-4 d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className={`badge rounded-pill px-3 ${note.isGeneral ? 'bg-warning text-dark' : 'bg-primary bg-opacity-10 text-primary'}`}>
                          {note.category || 'Allgemein'}
                        </span>
                        <div className="d-flex gap-2">
                          <button
                            className={`btn btn-link p-0 ${note.isGeneral ? 'text-warning' : 'text-muted opacity-50'}`}
                            onClick={(e) => handleTogglePin(e, note)}
                          >
                            <Pin size={16} fill={note.isGeneral ? "currentColor" : "none"} />
                          </button>
                          <button className="btn btn-link text-muted p-0" onClick={() => startEdit(note)}>
                            <Edit3 size={16} />
                          </button>
                        </div>
                      </div>
                      <h5 className="fw-bold text-dark mb-2">{note.title}</h5>
                      <div className="text-muted small flex-grow-1 overflow-hidden" style={{ maxHeight: '100px', whiteSpace: 'pre-wrap' }}>
                        {/* Simple preview - strip HTML for list view or just show raw if simple */}
                        {note.content ?
                          (note.content.includes('<') ? <div dangerouslySetInnerHTML={{ __html: note.content }} /> : note.content)
                          : <em className="opacity-50">Kein Inhalt</em>
                        }
                      </div>
                      <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-1 text-muted extra-small">
                          <Clock size={12} />
                          {new Date(note.noteDate).toLocaleDateString('de-DE')}
                        </div>
                        <button className="btn btn-link text-danger p-0" onClick={() => { if (window.confirm('Löschen?')) deleteNote(note.id) }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* --- NEW SEPARATED DIALOG --- */}
      <NoteDialog
        show={showAddForm}
        isEditing={isEditing}
        formData={formData}
        setFormData={setFormData}
        onSave={() => handleSave(formData)}
        onClose={resetForm}
        onExpand={() => handleExpandFromDialog(formData)}
      />

      <style>{`
        .hover-bg-light:hover { background-color: #f8f9fa; color: #0d6efd; }
        .hover-shadow:hover { transform: translateY(-3px); box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important; }
        .transition-all { transition: all 0.2s ease-in-out; }
        .extra-small { font-size: 0.75rem; }
        .pinned-active { background-color: #fffcf0 !important; border-left: 4px solid #ffc107 !important; }
      `}</style>
    </div>
  );
};

export default NotesManager;